import {
  RULE_CHANNEL_ID,
  RULE_DURATIONS_MILLISECONDS,
  RULE_KEY,
  RULE_LAST_UPDATED,
  RULE_SERIES_ID,
  RULE_LEAGUE_ID,
  RULE_SINGLE_PROGRAM,
  RULE_SINGLE_PROGRAM_DISPLAY,
  RULE_STATUS,
  RULE_STATUS_ACTIVE,
  RULE_STATUS_DISABLED,
  RULE_TYPE,
  RULE_TYPE_SERIES,
  RULE_TYPE_LEAGUE,
  STREAM_TYPE,
  RULE_PARTIAL_POD,
  RULE_PARTIAL_POD_DISPLAY,
  RULE_EVENT_ID,
  RULE_SPORTS_ID,
  RULE_TYPE_SPORTS,
  RULE_TYPE_EVENT,
  DURATION_APP,
  STREAM_TYPE_LINEAR,
  RULE_TYPE_CHANNEL,
  RULE_BACKTOBACKADS,
  RULE_BACKTOBACKADS_ENABLED,
} from './constants';
import { Tag } from 'antd';
import {
  DURATION_MANAGER_CHANNELS,
  DURATION_MANAGER_LEAGUES,
  DURATION_MANAGER_RULE_INFO_PATH_PREFIX,
  DURATION_MANAGER_SERIES,
  DURATION_MANAGER_SPORTS,
  DURATION_MANAGER_EVENT,
} from '../../constants';

export const buildHashKey = (streamType, ruleType, app, ...ids) => {
  let hashKey = `${streamType}#${ruleType}`;
  if (app && streamType === STREAM_TYPE_LINEAR && ruleType === RULE_TYPE_CHANNEL) {
    hashKey += `#${app.toUpperCase()}`;
  }
  ids.forEach((id) => {
    if (id) {
      hashKey += `#${id}`;
    }
  });
  return hashKey;
};

export const initRuleKey = (pathParams) => {
  if (!pathParams) {
    return null;
  }

  const { channelId, seriesId, leagueId } = pathParams;
  if (leagueId) {
    return `${channelId}#${leagueId}#LEAGUE`;
  } else if (seriesId) {
    return `${channelId}#${seriesId}`;
  } else {
    return channelId;
  }
};

export const convertRuleToTableData = ([, rule]) => ({
  key: rule[RULE_KEY],
  app: rule[DURATION_APP] || '',
  'stream-type': rule[STREAM_TYPE],
  'rule-type': rule[RULE_TYPE],
  ids: {
    'channel-id': rule[RULE_CHANNEL_ID],
    'series-id': rule[RULE_SERIES_ID],
    'league-id': rule[RULE_LEAGUE_ID],
    'event-id': rule[RULE_EVENT_ID],
    'sports-id': rule[RULE_SPORTS_ID],
  },
  'is-single-program': rule[RULE_SINGLE_PROGRAM] ? RULE_SINGLE_PROGRAM_DISPLAY : '-',
  'back-to-back-ads-enabled': rule[RULE_BACKTOBACKADS] === true,
  'is-partial-pods': rule[RULE_PARTIAL_POD] ? RULE_PARTIAL_POD_DISPLAY : '-',
  'durations-ms': rule[RULE_DURATIONS_MILLISECONDS].join(', '),
  status: rule[RULE_STATUS],
  'last-updated-date': rule[RULE_LAST_UPDATED],
});

export const convertRuleToCSVData = ([, rule]) => ({
  App: rule[DURATION_APP] ? rule[DURATION_APP].toUpperCase() : '',
  'Stream Type': rule[STREAM_TYPE],
  'Configuration Type': rule[RULE_TYPE],
  'Channel ID': rule[RULE_CHANNEL_ID],
  'Series ID': rule[RULE_SERIES_ID] ?? '',
  'League ID': rule[RULE_LEAGUE_ID] ?? '',
  'Event ID': rule[RULE_EVENT_ID] ?? '',
  'Sports ID': rule[RULE_SPORTS_ID] ?? '',
  'Duration(s)': rule[RULE_DURATIONS_MILLISECONDS].join('|'),
  'Single Program': rule[RULE_SINGLE_PROGRAM] ?? '',
  'Back to Back Ads': rule[RULE_BACKTOBACKADS] ?? '',
  'Partial Pod': rule[RULE_PARTIAL_POD] ?? '',
  Status: rule[RULE_STATUS],
});

export const buildRouteFromRow = (selectedRule) => {
  return `${DURATION_MANAGER_RULE_INFO_PATH_PREFIX}/${buildPathFromRule(selectedRule)}`;
};

export const buildPathFromRule = (selectedRule) => {
  const CHANNEL_ROUTE = selectedRule?.ids?.[RULE_CHANNEL_ID]
    ? `${DURATION_MANAGER_CHANNELS}/${selectedRule.ids[RULE_CHANNEL_ID]}`
    : null;
  const SERIES_ROUTE =
    selectedRule?.ids?.[RULE_CHANNEL_ID] && selectedRule?.ids?.[RULE_SERIES_ID]
      ? `${CHANNEL_ROUTE}/${DURATION_MANAGER_SERIES}/${selectedRule.ids[RULE_SERIES_ID]}`
      : null;
  const LEAGUE_ROUTE = selectedRule?.ids?.[RULE_LEAGUE_ID]
    ? `${DURATION_MANAGER_LEAGUES}/${selectedRule.ids[RULE_LEAGUE_ID]}`
    : null;
  const SPORTS_ROUTE = selectedRule?.ids?.[RULE_SPORTS_ID]
    ? `${DURATION_MANAGER_SPORTS}/${selectedRule.ids[RULE_SPORTS_ID]}`
    : null;
  const EVENT_ROUTE = selectedRule?.ids?.[RULE_EVENT_ID]
    ? `${DURATION_MANAGER_EVENT}/${selectedRule.ids[RULE_EVENT_ID]}`
    : null;

  switch (selectedRule[RULE_TYPE]) {
    case RULE_TYPE_SERIES:
      return SERIES_ROUTE;
    case RULE_TYPE_LEAGUE:
      return LEAGUE_ROUTE;
    case RULE_TYPE_SPORTS:
      return SPORTS_ROUTE;
    case RULE_TYPE_EVENT:
      return EVENT_ROUTE;
    default:
      return CHANNEL_ROUTE;
  }
};

export const ruleStatusRender = (status) => {
  switch (status) {
    case RULE_STATUS_ACTIVE:
      return (
        <Tag color="green">
          <b>Active</b>
        </Tag>
      );
    case RULE_STATUS_DISABLED:
      return (
        <Tag color="gray">
          <b>Disabled</b>
        </Tag>
      );
    default:
      return (
        <Tag color="red">
          <b>Unknown</b>
        </Tag>
      );
  }
};

export const labelRender = (label) => {
  return <b>{label}</b>;
};

const renderTag = (id, color, label) => {
  if (id != null) {
    return (
      <span>
        <Tag color={color}>
          <b>{label}</b>
        </Tag>
        {id}
        <p></p>
      </span>
    );
  }
  return null;
};

export const idRender = (text, { ids }) => {
  return (
    <div>
      {renderTag(ids[RULE_CHANNEL_ID], 'green', 'Channel Id')}
      {renderTag(ids[RULE_SERIES_ID], 'blue', 'Series Id')}
      {renderTag(ids[RULE_LEAGUE_ID], 'cyan', 'League Id')}
      {renderTag(ids[RULE_EVENT_ID], 'magenta', 'Event Id')}
      {renderTag(ids[RULE_SPORTS_ID], 'orange', 'Sports Id')}
    </div>
  );
};
